import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Avatar, Upload, Button, message as antMessage, Spin } from 'antd';
import { PlusOutlined, UserOutlined, PictureOutlined, FileOutlined, CloseOutlined } from '@ant-design/icons';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';

const { TextArea } = Input;
const { Option } = Select;

// Complaint Target Types
const TARGET_TYPES = {
    GENERAL: 0,
    SERVICE: 1,
    CLINIC: 2,
    DOCTOR: 3
};

const CreateOvrModal = ({ 
    visible, 
    onCancel, 
    onSubmit, 
    form, 
    staffData, 
    isLoading, 
    staffLoading 
}) => {
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);
    const [photoId, setPhotoId] = useState(null);
    const [docId, setDocId] = useState(null);
    const [docName, setDocName] = useState('');
    const [targetType, setTargetType] = useState(TARGET_TYPES.GENERAL);
    
    // Data states
    const [services, setServices] = useState([]);
    const [clinics, setClinics] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [patients, setPatients] = useState([]);
    
    // Loading states
    const [servicesLoading, setServicesLoading] = useState(false);
    const [clinicsLoading, setClinicsLoading] = useState(false);
    const [doctorsLoading, setDoctorsLoading] = useState(false);
    const [patientsLoading, setPatientsLoading] = useState(false);

    // Search state for patients
    const [patientSearchTimeout, setPatientSearchTimeout] = useState(null);
    
    // Complainant type: 'existing' or 'external'
    const [complainantType, setComplainantType] = useState(null);

    // Fetch services
    const fetchServices = async () => {
        setServicesLoading(true);
        try {
            const response = await drEasyApi.get('/api/Services/GetMyServices');
            setServices(response.data || []);
        } catch (err) {
            antMessage.error('Failed to load services');
            console.error(err);
        } finally {
            setServicesLoading(false);
        }
    };

    // Fetch clinics
    const fetchClinics = async () => {
        setClinicsLoading(true);
        try {
            const response = await drEasyApi.get('/api/Clinic/myCenter-clinics');
            setClinics(response.data?.data?.clinics || []);
        } catch (err) {
            antMessage.error('Failed to load clinics');
            console.error(err);
        } finally {
            setClinicsLoading(false);
        }
    };

    // Fetch doctors
    const fetchDoctors = async () => {
        setDoctorsLoading(true);
        try {
            const response = await drEasyApi.get('/api/Centers/getMyDoctors');
            setDoctors(response.data?.doctors || []);
        } catch (err) {
            antMessage.error('Failed to load doctors');
            console.error(err);
        } finally {
            setDoctorsLoading(false);
        }
    };

    // Fetch patients with optional search filter
    const fetchPatients = async (searchValue = '') => {
        setPatientsLoading(true);
        try {
            const params = searchValue ? { NameFilter: searchValue } : {};
            const response = await drEasyApi.get('/api/Centers/getPatients', { params });
            setPatients(response.data?.patients || []);
        } catch (err) {
            antMessage.error('Failed to load patients');
            console.error(err);
        } finally {
            setPatientsLoading(false);
        }
    };

    // Handle patient search with debouncing
    const handlePatientSearch = (value) => {
        // Clear existing timeout
        if (patientSearchTimeout) {
            clearTimeout(patientSearchTimeout);
        }

        // Set new timeout for debounced search
        const timeoutId = setTimeout(() => {
            fetchPatients(value);
        }, 500); // 500ms debounce

        setPatientSearchTimeout(timeoutId);
    };

    // Handle complainant type change
    const handleComplainantTypeChange = (value) => {
        setComplainantType(value);
        
        // Clear all complainant-related fields when switching types
        form.setFieldsValue({
            complainantUserId: null,
            externalName: null,
            externalPhone: null,
            externalEmail: null
        });
    };

    // Load initial data when modal opens
    useEffect(() => {
        if (visible && complainantType === 'existing') {
            fetchPatients(); // Load initial patients without filter
        }
    }, [visible, complainantType]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (patientSearchTimeout) {
                clearTimeout(patientSearchTimeout);
            }
        };
    }, [patientSearchTimeout]);

    // Handle target type change
    const handleTargetTypeChange = (value) => {
        setTargetType(value);
        
        // Clear related fields when target type changes
        form.setFieldsValue({
            serviceId: undefined,
            clinicId: undefined,
            doctorId: undefined
        });

        // Fetch data based on target type
        switch (value) {
            case TARGET_TYPES.SERVICE:
                if (services.length === 0) fetchServices();
                break;
            case TARGET_TYPES.CLINIC:
                if (clinics.length === 0) fetchClinics();
                break;
            case TARGET_TYPES.DOCTOR:
                if (doctors.length === 0) fetchDoctors();
                break;
            default:
                break;
        }
    };

    const handleImageUpload = async ({ file }) => {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await drEasyApi.post("/api/Storage/UploadImage", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const imageId = res.data.id;
            setPhotoId(imageId);
            form.setFieldsValue({ photoId: imageId });
            antMessage.success("Image uploaded successfully");
        } catch (err) {
            antMessage.error("Failed to upload image");
            console.error(err);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDocUpload = async ({ file }) => {
        setUploadingDoc(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            const res = await drEasyApi.post("/api/Storage/UploadPDF", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const documentId = res.data.id;
            setDocId(documentId);
            setDocName(file.name);
            form.setFieldsValue({ docId: documentId });
            antMessage.success("Document uploaded successfully");
        } catch (err) {
            antMessage.error("Failed to upload document");
            console.error(err);
        } finally {
            setUploadingDoc(false);
        }
    };

    const removeImage = () => {
        setPhotoId(null);
        form.setFieldsValue({ photoId: null });
    };

    const removeDoc = () => {
        setDocId(null);
        setDocName('');
        form.setFieldsValue({ docId: null });
    };

    const handleModalCancel = () => {
        setPhotoId(null);
        setDocId(null);
        setDocName('');
        setTargetType(TARGET_TYPES.GENERAL);
        setPatients([]); // Clear patients on close
        setComplainantType(null);
        onCancel();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            // Build the payload based on target type
            const payload = {
                title: values.title,
                body: values.body,
                photoId: photoId,
                docId: docId,
                adminUserId: values.adminUserId,
                secretaryUserId: values.secretaryUserId,
                targetType: targetType,
                complainantUserId: values.complainantUserId || null,
                externalName: values.externalName || null,
                externalPhone: values.externalPhone || null,
                externalEmail: values.externalEmail || null
            };

            // Add specific IDs based on target type
            switch (targetType) {
                case TARGET_TYPES.SERVICE:
                    payload.serviceId = values.serviceId;
                    break;
                case TARGET_TYPES.CLINIC:
                    payload.clinicId = values.clinicId;
                    break;
                case TARGET_TYPES.DOCTOR:
                    payload.doctorId = values.doctorId;
                    break;
                default:
                    break;
            }

            await onSubmit(payload);
        } catch (err) {
            console.error('Validation failed:', err);
        }
    };

    return (
        <Modal
            title={
                <div className="flex items-center gap-2 text-lg">
                    <PlusOutlined className="text-[#298d9f]" />
                    <span>Create New OVR</span>
                </div>
            }
            open={visible}
            onCancel={handleModalCancel}
            footer={[
                <button
                    key="cancel"
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded"
                    onClick={handleModalCancel}
                >
                    Cancel
                </button>,
                <button
                    key="submit"
                    className="px-4 py-2 bg-[#298d9f] hover:bg-[#236d7c] text-white rounded ml-2"
                    onClick={handleSubmit}
                    disabled={isLoading || uploadingImage || uploadingDoc}
                >
                    {isLoading ? 'Creating...' : 'Create'}
                </button>,
            ]}
            width={700}
            destroyOnClose
        >
            <Form form={form} layout="vertical" className="mt-4">
                <Form.Item
                    name="title"
                    label="Title"
                    rules={[{ required: true, message: 'Please enter a title' }]}
                >
                    <Input placeholder="Enter OVR title" size="large" />
                </Form.Item>

                <Form.Item
                    name="body"
                    label="Description"
                    rules={[{ required: true, message: 'Please enter a description' }]}
                >
                    <TextArea 
                        rows={2} 
                        placeholder="Enter detailed description"
                        size="large"
                    />
                </Form.Item>

                {/* Complaint Target Type */}
                <Form.Item
                    name="targetType"
                    label="Complaint Type"
                    rules={[{ required: true, message: 'Please select a complaint type' }]}
                    initialValue={TARGET_TYPES.GENERAL}
                >
                    <Select 
                        placeholder="Select complaint type"
                        size="large"
                        onChange={handleTargetTypeChange}
                    >
                        <Option value={TARGET_TYPES.GENERAL}>General</Option>
                        <Option value={TARGET_TYPES.SERVICE}>Service</Option>
                        <Option value={TARGET_TYPES.CLINIC}>Clinic</Option>
                        <Option value={TARGET_TYPES.DOCTOR}>Doctor</Option>
                    </Select>
                </Form.Item>

                {/* Conditional Fields Based on Target Type */}
                {targetType === TARGET_TYPES.SERVICE && (
                    <Form.Item
                        name="serviceId"
                        label="Select Service"
                        rules={[{ required: true, message: 'Please select a service' }]}
                    >
                        <Select 
                            placeholder="Select service"
                            showSearch
                            size="large"
                            loading={servicesLoading}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {services.map(service => (
                                <Option key={service.id} value={service.id}>
                                    {service.title || service.titleEn}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {targetType === TARGET_TYPES.CLINIC && (
                    <Form.Item
                        name="clinicId"
                        label="Select Clinic"
                        rules={[{ required: true, message: 'Please select a clinic' }]}
                    >
                        <Select 
                            placeholder="Select clinic"
                            showSearch
                            size="large"
                            loading={clinicsLoading}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {clinics.map(clinic => (
                                <Option key={clinic.clinicId} value={clinic.clinicId}>
                                    {clinic.clinicName}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {targetType === TARGET_TYPES.DOCTOR && (
                    <Form.Item
                        name="doctorId"
                        label="Select Doctor"
                        rules={[{ required: true, message: 'Please select a doctor' }]}
                    >
                        <Select 
                            placeholder="Select doctor"
                            showSearch
                            size="large"
                            loading={doctorsLoading}
                            filterOption={(input, option) =>
                                option.children.props.children[1].props.children
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                            }
                        >
                            {doctors.map(doctor => (
                                <Option key={doctor.doctorId} value={doctor.doctorId}>
                                    <div className="flex items-center gap-2">
                                        <Avatar 
                                            size={24} 
                                            src={doctor.photoId ? getImg(doctor.photoId) : null}
                                            icon={!doctor.photoId ? <UserOutlined /> : null}
                                            className="bg-[#298d9f]" 
                                        />
                                        <span>{doctor.name}</span>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {/* Complainant Type Selection */}
                <Form.Item
                    name="complainantType"
                    label="Complainant to Patient?"
                >
                    <Select 
                        placeholder="Select if existing patient or external"
                        size="large"
                        onChange={handleComplainantTypeChange}
                    >
                        <Option value="existing">Existing Patient</Option>
                        <Option value="external">External Complainant</Option>
                    </Select>
                </Form.Item>

                {/* Existing Patient Selection */}
                {complainantType === 'existing' && (
                    <Form.Item
                        name="complainantUserId"
                        label="Select Patient"
                    >
                        <Select 
                            placeholder="Search for patient by name..."
                            allowClear
                            showSearch
                            size="large"
                            loading={patientsLoading}
                            onSearch={handlePatientSearch}
                            filterOption={false} // Disable client-side filtering
                            notFoundContent={patientsLoading ? <Spin size="small" /> : 'No patients found'}
                        >
                            {patients.map(patient => (
                                <Option key={patient.accountId} value={patient.accountId}>
                                    <div className="flex items-center gap-2">
                                        <Avatar 
                                            size={24} 
                                            src={patient.imageId ? getImg(patient.imageId) : null}
                                            icon={!patient.imageId ? <UserOutlined /> : null}
                                            className="bg-blue-500" 
                                        />
                                        <span>{patient.fullName || patient.phoneNumber || patient.email}</span>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {/* External Complainant Info */}
                {complainantType === 'external' && (
                    <div className="bg-gray-50 p-4 rounded mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                            External Complainant Information
                        </h4>
                        <div className="space-y-3">
                            <Form.Item 
                                name="externalName" 
                                className="mb-0"
                            >
                                <Input 
                                    placeholder="External complainant name" 
                                    size="large"
                                />
                            </Form.Item>
                            <Form.Item 
                                name="externalPhone" 
                                className="mb-0"
                            >
                                <Input 
                                    placeholder="External complainant phone" 
                                    size="large"
                                />
                            </Form.Item>
                            <Form.Item 
                                name="externalEmail" 
                                className="mb-0"
                            >
                                <Input 
                                    placeholder="External complainant email (optional)" 
                                    size="large" 
                                    type="email"
                                />
                            </Form.Item>
                        </div>
                    </div>
                )}

                {/* Attachments Section */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Attachments (Optional)
                    </label>
                    
                    <div className="flex gap-2 mb-3">
                        <Upload
                            showUploadList={false}
                            customRequest={handleImageUpload}
                            accept=".jpg,.jpeg,.png,.gif,.webp"
                            disabled={uploadingImage || uploadingDoc}
                        >
                            <Button
                                icon={uploadingImage ? <Spin size="small" /> : <PictureOutlined />}
                                disabled={uploadingImage || uploadingDoc}
                            >
                                Upload Image
                            </Button>
                        </Upload>

                        <Upload
                            showUploadList={false}
                            customRequest={handleDocUpload}
                            accept=".pdf"
                            disabled={uploadingImage || uploadingDoc}
                        >
                            <Button
                                icon={uploadingDoc ? <Spin size="small" /> : <FileOutlined />}
                                disabled={uploadingImage || uploadingDoc}
                            >
                                Upload PDF
                            </Button>
                        </Upload>
                    </div>

                    {/* Preview Attachments */}
                    {(photoId || docId) && (
                        <div className="space-y-2">
                            {photoId && (
                                <div className="relative inline-block">
                                    <img
                                        src={getImg(photoId)}
                                        alt="Preview"
                                        className="rounded max-h-32 border border-gray-200"
                                    />
                                    <Button
                                        icon={<CloseOutlined />}
                                        size="small"
                                        danger
                                        className="absolute top-1 right-1"
                                        onClick={removeImage}
                                    />
                                </div>
                            )}
                            
                            {docId && (
                                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded p-2">
                                    <FileOutlined className="text-blue-600" />
                                    <span className="text-sm text-gray-700 flex-1">{docName}</span>
                                    <Button
                                        icon={<CloseOutlined />}
                                        size="small"
                                        danger
                                        type="text"
                                        onClick={removeDoc}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Hidden fields */}
                <Form.Item name="photoId" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="docId" hidden>
                    <Input />
                </Form.Item>

                {/* Staff Assignment */}
                <Form.Item
                    name="adminUserId"
                    label="Assign to Admin"
                    rules={[{ required: true, message: 'Please select an admin' }]}
                >
                    <Select 
                        placeholder="Select admin"
                        showSearch
                        size="large"
                        loading={staffLoading}
                        filterOption={(input, option) =>
                            option.children.props.children[1].props.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {staffData?.staff
                            ?.filter(s => s.role === 3 || s.roleStr === 'CenterAdmin')
                            .map(admin => (
                                <Option key={admin.userId} value={admin.userId}>
                                    <div className="flex items-center gap-2">
                                        <Avatar size={24} icon={<UserOutlined />} className="bg-[#298d9f]" />
                                        <span>{admin.name}</span>
                                    </div>
                                </Option>
                            ))
                        }
                    </Select>
                </Form.Item>

                <Form.Item
                    name="secretaryUserId"
                    label="Assign to Secretary"
                    rules={[{ required: true, message: 'Please select a Secretary' }]}
                >
                    <Select 
                        placeholder="Select secretary"
                        allowClear
                        showSearch
                        size="large"
                        loading={staffLoading}
                        filterOption={(input, option) =>
                            option.children.props.children[1].props.children
                                .toLowerCase()
                                .includes(input.toLowerCase())
                        }
                    >
                        {staffData?.staff
                            ?.filter(s => s.role === 4 || s.roleStr === 'Secretary')
                            .map(secretary => (
                                <Option key={secretary.userId} value={secretary.userId}>
                                    <div className="flex items-center gap-2">
                                        <Avatar size={24} icon={<UserOutlined />} className="bg-green-600" />
                                        <span>{secretary.name}</span>
                                    </div>
                                </Option>
                            ))
                        }
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateOvrModal;